import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowDown, Download, Minus, Plus, RotateCcw, X } from 'lucide-react';

interface PDFViewerProps {
  pdfData: string;
  fileName?: string;
  onClose?: () => void;
}

export function PDFViewer({ pdfData, fileName, onClose }: PDFViewerProps) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scale, setScale] = useState(1.0); // Default scale (100%)
  const [documentId, setDocumentId] = useState<number | null>(null);
  const [isUsingFallback, setIsUsingFallback] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Extract document ID from URL if available
  useEffect(() => {
    if (pdfData && pdfData.includes('/api/documents/')) {
      const match = pdfData.match(/\/api\/documents\/(\d+)/);
      if (match && match[1]) {
        const id = parseInt(match[1], 10);
        if (!isNaN(id)) {
          console.log(`Document ID extracted from URL: ${id}`);
          setDocumentId(id);
        }
      }
    }
  }, [pdfData]);

  // Function to create a URL for the PDF from data URL or API path
  useEffect(() => {
    if (!pdfData) {
      setError('No PDF data provided');
      setIsLoading(false);
      return;
    }

    try {
      console.log(`Setting up PDF viewer for: ${fileName || 'unnamed document'}`);

      // If the PDF data starts with '/api/', it's a server path, not a data URL
      // In this case, we'll use the path directly
      if (pdfData.startsWith('/api/')) {
        console.log(`Using API path directly: ${pdfData}`);
        // Add cache-busting parameter to ensure we get the latest version
        setPdfUrl(`${pdfData}${pdfData.includes('?') ? '&' : '?'}_t=${Date.now()}`);
        return;
      }

      // If the PDF data is a data URL
      if (pdfData.startsWith('data:application/pdf')) {
        console.log('Using PDF data URL directly');
        setPdfUrl(pdfData);
        return;
      }

      // If it's a document ID (number only)
      if (/^\d+$/.test(pdfData)) {
        console.log(`Using numeric document ID: ${pdfData}`);
        setPdfUrl(`/api/documents/${pdfData}?_t=${Date.now()}`);
        setDocumentId(parseInt(pdfData, 10));
        return;
      }

      // Handle other URL formats
      if (pdfData.includes('/document/') || pdfData.includes('/api/document-fallback/')) {
        console.log(`Using special document URL: ${pdfData}`);
        setPdfUrl(`${pdfData}${pdfData.includes('?') ? '&' : '?'}_t=${Date.now()}`);
        return;
      }

      console.warn('Invalid PDF data format:', pdfData);
      setError('Invalid PDF data format');

      // If we have a document ID, automatically try fallback
      if (documentId) {
        console.log(`Automatically trying fallback for document ID: ${documentId}`);
        setTimeout(() => tryFallbackPdf(), 500);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error preparing PDF for display:', error);
      setError('Failed to prepare PDF for display');
      setIsLoading(false);
    }
  }, [pdfData, fileName]);

  function zoomIn() {
    setScale((prevScale) => {
      const newScale = Math.min(prevScale + 0.25, 3); // Maximum zoom: 300%
      updateIframeZoom(newScale);
      return newScale;
    });
  }

  function zoomOut() {
    setScale((prevScale) => {
      const newScale = Math.max(prevScale - 0.25, 0.5); // Minimum zoom: 50%
      updateIframeZoom(newScale);
      return newScale;
    });
  }

  function resetZoom() {
    setScale(1.0);
    updateIframeZoom(1.0);
  }

  function updateIframeZoom(newScale: number) {
    const iframe = iframeRef.current;
    if (iframe) {
      try {
        const contentDoc = iframe.contentDocument;
        if (contentDoc) {
          // Try to zoom the PDF content inside the iframe
          // This works in many browsers, but not all
          try {
            const viewer = contentDoc.querySelector('.pdfViewer') as HTMLElement;
            if (viewer) {
              viewer.style.transform = `scale(${newScale})`;
              viewer.style.transformOrigin = 'center top';
            } else {
              // Fallback: try to scale the entire document
              const body = contentDoc.body;
              if (body) {
                body.style.zoom = String(newScale);
                // Alternative scaling for Firefox
                body.style.transform = `scale(${newScale})`;
                body.style.transformOrigin = 'center top';
              }
            }
          } catch (e) {
            console.warn('Failed to zoom PDF content:', e);
          }
        }
      } catch (e) {
        console.warn('Cannot access iframe content document for zooming:', e);
      }
    }
  }

  function retryLoading() {
    setIsLoading(true);
    setError(null);

    // Add a timestamp to force reload and bypass cache
    if (pdfUrl && pdfUrl.startsWith('/api/')) {
      const baseUrl = pdfUrl.split('?')[0];
      setPdfUrl(`${baseUrl}?_t=${Date.now()}`);
    } else if (documentId) {
      setPdfUrl(`/api/documents/${documentId}?_t=${Date.now()}`);
    }

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set a new timeout to check loading state
    timeoutRef.current = setTimeout(() => {
      if (isLoading) {
        setIsLoading(false);
        setError('Loading timed out. The PDF may be corrupted or too large.');
      }
    }, 10000); // 10 second timeout
  }

  function tryFallbackPdf() {
    setIsLoading(true);
    setError(null);
    setIsUsingFallback(true);

    // Try to fetch a fallback version with compatibility mode
    if (documentId) {
      const fallbackUrl = `/api/documents/${documentId}/fallback?_t=${Date.now()}`;
      console.log(`Trying fallback PDF URL: ${fallbackUrl}`);
      setPdfUrl(fallbackUrl);
    } else {
      setIsLoading(false);
      setError('Cannot load fallback without document ID');
    }
  }

  function downloadPdf() {
    // For direct data URLs, create a temporary link and trigger download
    if (pdfData.startsWith('data:')) {
      const downloadLink = document.createElement('a');
      downloadLink.href = pdfData;
      downloadLink.download = fileName || 'document.pdf';
      downloadLink.click();
      return;
    }

    // For API URLs, open in a new tab with download trigger
    if (pdfData.startsWith('/api/')) {
      const downloadUrl = `${pdfData}${pdfData.includes('?') ? '&' : '?'}download=true`;
      window.open(downloadUrl, '_blank');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-background rounded-lg w-[90%] h-[90%] flex flex-col overflow-hidden">
        {/* PDF Viewer header with controls */}
        <div className="flex items-center justify-between p-2 bg-muted/50 border-b">
          <div className="flex-1 truncate ml-2">
            <h3 className="text-md font-medium">{fileName || 'PDF Document'}</h3>
          </div>

          <div className="flex items-center gap-2">
            <Button onClick={zoomOut} size="icon" variant="outline" title="Zoom out">
              <Minus className="h-4 w-4" />
            </Button>

            <Button onClick={resetZoom} size="icon" variant="outline" title="Reset zoom">
              <span className="text-xs font-medium">{Math.round(scale * 100)}%</span>
            </Button>

            <Button onClick={zoomIn} size="icon" variant="outline" title="Zoom in">
              <Plus className="h-4 w-4" />
            </Button>

            <Button onClick={downloadPdf} size="icon" variant="outline" title="Download PDF">
              <Download className="h-4 w-4" />
            </Button>

            {onClose && (
              <Button onClick={onClose} size="icon" variant="outline" title="Close PDF">
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* PDF content area */}
        <div className="flex-1 overflow-hidden bg-muted/20 relative">
          {/* Loading indicator */}
          {isLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/80 z-10">
              <div className="h-10 w-10 rounded-full border-4 border-primary/30 border-t-primary animate-spin"></div>
              <p className="mt-4 text-sm text-muted-foreground">Loading document...</p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/95 z-10">
              <div className="max-w-md text-center space-y-4">
                <p className="text-destructive">{error}</p>

                <div className="flex flex-wrap gap-2 justify-center mt-4">
                  <Button onClick={retryLoading} variant="outline" size="sm">
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Retry
                  </Button>

                  {documentId && !isUsingFallback && (
                    <Button onClick={tryFallbackPdf} variant="outline" size="sm">
                      <ArrowDown className="h-4 w-4 mr-2" />
                      Try Compatibility Mode
                    </Button>
                  )}

                  <Button onClick={downloadPdf} variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* PDF Viewer using iframe instead of object for better browser compatibility */}
          {!isLoading && !error && pdfUrl && (
            <iframe
              ref={iframeRef}
              src={pdfUrl}
              className="w-full h-full border-0"
              title="PDF Document"
              onLoad={() => {
                // Mark as loaded successfully
                setIsLoading(false);
                console.log('PDF loaded successfully');
              }}
              onError={() => {
                console.error('PDF iframe onError triggered');
                setError(
                  isUsingFallback
                    ? 'Failed to load the fallback PDF. The document may be severely corrupted.'
                    : 'Failed to load the PDF. Try the fallback version or download it instead.'
                );
                setIsLoading(false);

                // If this is the first error and we have documentId, auto-switch to fallback
                if (!isUsingFallback && documentId && !error) {
                  console.log('PDF failed to load - will try fallback');
                  setTimeout(() => tryFallbackPdf(), 500);
                }
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
